import type { FunctionComponent } from 'react';
import track from 'react-tracking';
import { Table } from './styled';
import PositiveNegativeValue from '@components/PositiveNegativeValue';
import { SubSubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSubSectionHeader';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

interface Props {
  exchangeCountryISO: string;
  industryName: string;
  uniqueSymbol: string;
  company7D: number | null;
  company7DFormatted: string;
  company1YrAbs: number | null;
  company1YrAbsFormatted: string;
  industry7D: number | null;
  industry7DFormatted: string;
  industry1YrAbs: number | null;
  industry1YrAbsFormatted: string;
  market7D: number | null;
  market7DFormatted: string;
  market1YrAbs: number | null;
  market1YrAbsFormatted: string;
}

const ShareHolderReturnsSummary: FunctionComponent<Props> = ({
  exchangeCountryISO,
  industryName,
  uniqueSymbol,
  company7D,
  company7DFormatted,
  company1YrAbs,
  company1YrAbsFormatted,
  industry7D,
  industry7DFormatted,
  industry1YrAbs,
  industry1YrAbsFormatted,
  market7D,
  market7DFormatted,
  market1YrAbs,
  market1YrAbsFormatted,
}) => {
  const t = useOverviewTranslation();
  return (
    <>
      <SubSubSectionHeader>
        {t('pricePerformance.shareReturns.title')}
      </SubSubSectionHeader>
      <Table data-cy-id="side-panel-perf-table">
        <thead>
          <tr>
            <th></th>
            <th>{uniqueSymbol}</th>
            <th>{`${exchangeCountryISO} ${industryName}`}</th>
            <th>
              {t('pricePerformance.shareReturns.table.market', {
                exchangeCountryISO,
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>7D</td>
            <PositiveNegativeValue
              as="td"
              value={company7D}
              data-cy-id="sharehold-return-company-7d"
            >
              {company7DFormatted}
            </PositiveNegativeValue>
            <PositiveNegativeValue
              as="td"
              value={industry7D}
              data-cy-id="sharehold-return-industry-7d"
            >
              {industry7DFormatted}
            </PositiveNegativeValue>
            <PositiveNegativeValue
              as="td"
              value={market7D}
              data-cy-id="sharehold-return-market-7d"
            >
              {market7DFormatted}
            </PositiveNegativeValue>
          </tr>
          <tr>
            <td>1Y</td>
            <PositiveNegativeValue as="td" value={company1YrAbs}>
              {company1YrAbsFormatted}
            </PositiveNegativeValue>
            <PositiveNegativeValue as="td" value={industry1YrAbs}>
              {industry1YrAbsFormatted}
            </PositiveNegativeValue>
            <PositiveNegativeValue as="td" value={market1YrAbs}>
              {market1YrAbsFormatted}
            </PositiveNegativeValue>
          </tr>
        </tbody>
      </Table>
    </>
  );
};

export default track({
  type: 'track',
  subject: 'shareholder_returns_summary',
})(ShareHolderReturnsSummary) as typeof ShareHolderReturnsSummary;
